import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Popup from 'reactjs-popup';
import axios from 'axios';
import { isJsonPayload } from '../../../../shared/api/payload';
import { toast } from 'react-toastify';
import { canCreateConfirm } from '../../../../shared/offerConfirmEligibility';
import { actionErrorText } from '../../../../shared/toastHelper';

const SimpleArtistMenu = ({ ctrl, row, onEdit }) => {
  const [openSub, setOpenSub] = useState(null);

  const team = ctrl.team;
  const isActive = row.id === ctrl.offer?.id;

  // Settlement is meaningless until the offer is linked to an event (the
  // settlement renderer is built around the calendar_event); without one it
  // would hard-error. Lineup siblings share the active offer's event linkage.
  const hasLinkedEvent = !!ctrl.offer?.calendar_event_id;
  const settlementTooltip = 'Available once the offer is linked to an event';

  const handleRemove = (close) => {
    close();
    if (isActive) {
      ctrl.handleDelete();
      return;
    }
    const name = row.performer_name || 'this artist';
    if (window.confirm(`Remove ${name}? This cannot be undone.`)) {
      axios
        .delete(`/teams/${team.id}/offers/v3/${row.id}.json`, {
          headers: { 'X-CSRF-TOKEN': ctrl.csrfToken },
        })
        .then(({ data }) => {
          if (!isJsonPayload(data)) {
            toast.error('Failed to remove artist. Please reload the page and try again.');
            return;
          }
          window.location.reload();
        })
        .catch((e) => toast.error(actionErrorText(e, 'Failed to remove artist. Please try again.')));
    }
  };

  const trigger = (
    <button
      type="button"
      data-testid="artist-menu-trigger"
      className="simple-artist-menu__trigger"
      aria-label={`Actions for ${row.performer_name || 'artist'}`}
      aria-haspopup="menu"
    >
      Actions
      <i className="far fa-chevron-down" aria-hidden="true" />
    </button>
  );

  return (
    <Popup
      trigger={trigger}
      on="click"
      position="bottom right"
      arrow={false}
      closeOnDocumentClick
      nested
      contentStyle={{
        width: 200,
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(10,13,18,0.12)',
        border: '1px solid #E9EAEB',
        padding: 0,
        overflow: 'visible',
      }}
    >
      {(close) => (
        <div
          className="simple-artist-menu"
          onMouseLeave={() => setOpenSub(null)}
        >
          {/* Edit */}
          <button
            type="button"
            data-testid="menu-edit"
            className="simple-artist-menu__item"
            onClick={() => {
              close();
              if (isActive) {
                onEdit();
              } else {
                // Navigate to the sibling's editor with a flag so the deal form
                // auto-opens on load (no second trip to the overflow menu).
                window.location.href = `/teams/${team.id}/offers/v3_simple/${row.id}/edit?edit=1`;
              }
            }}
          >
            Edit
          </button>

          {/* Offer submenu parent */}
          <div className="simple-artist-menu__submenu-wrap">
            <button
              type="button"
              data-testid="menu-offer"
              className="simple-artist-menu__item simple-artist-menu__item--parent"
              onMouseEnter={() => setOpenSub('offer')}
              onClick={() => setOpenSub((s) => (s === 'offer' ? null : 'offer'))}
            >
              Offer <span className="simple-artist-menu__caret">›</span>
            </button>
            {openSub === 'offer' && (
              <div className="simple-artist-menu__submenu">
                <button
                  type="button"
                  data-testid="menu-offer-preview"
                  className="simple-artist-menu__item"
                  onClick={() => {
                    close();
                    setOpenSub(null);
                    window.open(`/teams/${team.id}/offers/v3/${row.id}`, '_blank');
                  }}
                >
                  Preview
                </button>
                <button
                  type="button"
                  data-testid="menu-offer-download"
                  className="simple-artist-menu__item"
                  onClick={() => {
                    close();
                    setOpenSub(null);
                    window.open(`/teams/${team.id}/offers/v3/${row.id}/preview.pdf`, '_blank');
                  }}
                >
                  Download
                </button>
                <button
                  type="button"
                  data-testid="menu-offer-send"
                  className="simple-artist-menu__item"
                  onClick={() => {
                    close();
                    setOpenSub(null);
                    if (isActive) {
                      ctrl.handleSendOffer();
                    } else {
                      window.location.href = `/teams/${team.id}/offers/v3_simple/${row.id}/edit`;
                    }
                  }}
                >
                  Send Offer
                </button>
                {isActive && canCreateConfirm(ctrl.offer, ctrl.currentUser) && (
                  <button
                    type="button"
                    data-testid="menu-offer-create-confirm"
                    className="simple-artist-menu__item"
                    onClick={() => {
                      close();
                      setOpenSub(null);
                      ctrl.setCreateConfirmOpen(true);
                    }}
                  >
                    Create a Confirm
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Settlement submenu parent */}
          <div className="simple-artist-menu__submenu-wrap">
            <button
              type="button"
              data-testid="menu-settlement"
              className="simple-artist-menu__item simple-artist-menu__item--parent"
              onMouseEnter={() => setOpenSub('settlement')}
              onClick={() => setOpenSub((s) => (s === 'settlement' ? null : 'settlement'))}
            >
              Settlement <span className="simple-artist-menu__caret">›</span>
            </button>
            {openSub === 'settlement' && (
              <div className="simple-artist-menu__submenu">
                <button
                  type="button"
                  data-testid="menu-settlement-preview"
                  className="simple-artist-menu__item"
                  disabled={!hasLinkedEvent}
                  title={hasLinkedEvent ? undefined : settlementTooltip}
                  onClick={() => {
                    close();
                    setOpenSub(null);
                    window.open(`/teams/${team.id}/offers/v3/${row.id}/settlement`, '_blank');
                  }}
                >
                  Preview
                </button>
                <button
                  type="button"
                  data-testid="menu-settlement-download"
                  className="simple-artist-menu__item"
                  disabled={!hasLinkedEvent}
                  title={hasLinkedEvent ? undefined : settlementTooltip}
                  onClick={() => {
                    close();
                    setOpenSub(null);
                    window.open(`/teams/${team.id}/offers/v3/${row.id}/settlement.pdf`, '_blank');
                  }}
                >
                  Download
                </button>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="simple-artist-menu__divider" />

          {/* Remove artist */}
          <button
            type="button"
            data-testid="menu-remove"
            className="simple-artist-menu__item simple-artist-menu__item--danger"
            onClick={() => handleRemove(close)}
          >
            Remove artist
          </button>
        </div>
      )}
    </Popup>
  );
};

SimpleArtistMenu.propTypes = {
  ctrl: PropTypes.object.isRequired,
  row: PropTypes.shape({
    id: PropTypes.number,
    performer_name: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func,
};

SimpleArtistMenu.defaultProps = {
  onEdit: () => {},
};

export default SimpleArtistMenu;
